import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import ContentHeader from '../components/ContentHeader';
import { coverageAPI, staffAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import './ActingScCoveragePage.css';

function staffLabel(s) {
  const ln = (s.last_name || '').trim();
  const fn = (s.first_name || '').trim();
  return [ln, fn].filter(Boolean).join(', ') || `Staff #${s.id}`;
}

export default function ActingScCoveragePage() {
  const { tenantPath } = useTenantFromUrl();
  const canManage = hasPermission('caseload.manage_acting_sc_coverage');

  const [staffList, setStaffList] = useState([]);

  // Setup section
  const [selectedScId, setSelectedScId] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [scClients, setScClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsLoaded, setClientsLoaded] = useState(false);

  // pending[clientId] = covering staff id string ('' = no change / keep existing)
  const [pending, setPending] = useState({});
  const [bulkCoveringId, setBulkCoveringId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Active coverage section
  const [activeRows, setActiveRows] = useState([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [activeError, setActiveError] = useState(null);
  const [activeSort, setActiveSort] = useState({ col: null, dir: 'asc' });
  const [selectedIds, setSelectedIds] = useState(new Set());

  const staffOptions = useMemo(
    () => [...staffList].sort((a, b) => staffLabel(a).localeCompare(staffLabel(b), undefined, { sensitivity: 'base' })),
    [staffList]
  );

  useEffect(() => {
    staffAPI.getAll({ active: 'true' }).then((res) => {
      const data = res.data?.data ?? res.data;
      setStaffList(Array.isArray(data) ? data : []);
    }).catch(() => setStaffList([]));
  }, []);

  const loadActive = useCallback(async () => {
    try {
      setLoadingActive(true);
      setActiveError(null);
      const res = await coverageAPI.getActive();
      setActiveRows(res.data?.data || []);
    } catch (err) {
      setActiveError(err.response?.data?.error || err.message || 'Failed to load active coverage');
    } finally {
      setLoadingActive(false);
    }
  }, []);

  useEffect(() => { loadActive(); }, [loadActive]);

  const handleLoadClients = async () => {
    if (!selectedScId) return;
    setLoadingClients(true);
    setClientsLoaded(false);
    setPending({});
    setBulkCoveringId('');
    setSaveError(null);
    try {
      const res = await coverageAPI.getClientsForSc(selectedScId);
      setScClients(res.data?.data || []);
      setClientsLoaded(true);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const handleBulkApply = () => {
    if (!bulkCoveringId) return;
    const updates = {};
    scClients.forEach((c) => {
      if (!c.covering_staff_id) {
        updates[c.id] = bulkCoveringId;
      }
    });
    setPending((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // Group pending assignments by covering staff id
      const byStaff = {};
      for (const [clientId, coveringId] of Object.entries(pending)) {
        if (!coveringId) continue;
        if (!byStaff[coveringId]) byStaff[coveringId] = [];
        byStaff[coveringId].push(clientId);
      }
      for (const [coveringId, clientIds] of Object.entries(byStaff)) {
        await coverageAPI.createAssignments({
          client_ids: clientIds,
          covering_staff_id: parseInt(coveringId, 10),
          expected_end_at: expectedReturn ? `${expectedReturn}T00:00:00` : undefined,
        });
      }
      // Reload both sections
      await handleLoadClients();
      await loadActive();
      setPending({});
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || 'Failed to save coverage');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSelected = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Remove ${ids.length} coverage assignment${ids.length !== 1 ? 's' : ''}? The covering SCs will no longer see these clients in their caseload.`)) return;
    try {
      await Promise.all(ids.map((id) => coverageAPI.removeAssignment(id)));
      setSelectedIds(new Set());
      await loadActive();
      if (clientsLoaded) await handleLoadClients();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to remove coverage assignments');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(sortedActiveRows.map((r) => r.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const pendingCount = Object.values(pending).filter(Boolean).length;

  const sortedActiveRows = useMemo(() => {
    if (!activeSort.col) return activeRows;
    const getValue = (r) => {
      if (activeSort.col === 'client') return `${r.client_last_name || ''} ${r.client_first_name || ''}`.trim().toLowerCase();
      if (activeSort.col === 'primary') return `${r.primary_staff_last_name || ''} ${r.primary_staff_first_name || ''}`.trim().toLowerCase();
      if (activeSort.col === 'covering') return `${r.covering_staff_last_name || ''} ${r.covering_staff_first_name || ''}`.trim().toLowerCase();
      if (activeSort.col === 'return') return r.expected_end_at || '';
      return '';
    };
    return [...activeRows].sort((a, b) => {
      const cmp = getValue(a).localeCompare(getValue(b));
      return activeSort.dir === 'asc' ? cmp : -cmp;
    });
  }, [activeRows, activeSort]);

  const handleActiveSort = (col) => {
    setActiveSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  const sortArrow = (col) => {
    if (activeSort.col !== col) return <span className="coverage-sort-arrow coverage-sort-none">↕</span>;
    return <span className="coverage-sort-arrow">{activeSort.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  const currentCoveringLabel = (client) => {
    const eff = pending[client.id];
    if (eff !== undefined) {
      if (!eff) return null;
      const s = staffList.find((x) => String(x.id) === String(eff));
      return s ? staffLabel(s) : null;
    }
    if (client.covering_staff_id) {
      return [client.covering_staff_last_name, client.covering_staff_first_name].filter(Boolean).join(', ');
    }
    return null;
  };

  if (!canManage) {
    return (
      <div className="coverage-page">
        <ContentHeader title="SC Coverage" breadcrumbs={[{ label: 'Lists', to: tenantPath('/lists') }]} />
        <p className="coverage-muted" style={{ marginTop: '1rem' }}>
          You do not have permission to manage SC coverage.
        </p>
      </div>
    );
  }

  return (
    <div className="coverage-page">
      <ContentHeader
        title="SC Coverage"
        breadcrumbs={[{ label: 'Lists', to: tenantPath('/lists') }]}
        titleHint={
          <>
            <p>Assign temporary coverage when an SC is away — spread their clients across other SCs.</p>
            <p>
              Coverage ends <strong>automatically</strong> at the end of the <strong>expected return date</strong>:
              on that day the covering SC still sees the client, and starting the next day the assignment drops off
              this list and the covering SC no longer sees the client.
            </p>
            <p>
              If an SC returns early, you don&apos;t have to wait — select the rows under{' '}
              <strong>Active coverage</strong> and click <strong>Remove selected</strong> to end coverage right away.
              Coverage with no return date stays in place until you remove it manually.
            </p>
          </>
        }
      />

      {/* ── Set up coverage ── */}
      <section className="coverage-card">
        <h2 className="coverage-section-title">Set up coverage</h2>

        <div className="coverage-setup-row">
          <label className="coverage-label">
            SC going away
            <select
              value={selectedScId}
              onChange={(e) => { setSelectedScId(e.target.value); setClientsLoaded(false); setPending({}); }}
              className="coverage-select"
            >
              <option value="">Select SC…</option>
              {staffOptions.map((s) => (
                <option key={s.id} value={String(s.id)}>{staffLabel(s)}</option>
              ))}
            </select>
          </label>

          <label className="coverage-label">
            Expected return date
            <input
              type="date"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value)}
              className="coverage-date-input"
            />
          </label>

          <button
            type="button"
            className="btn-save coverage-load-btn"
            onClick={handleLoadClients}
            disabled={!selectedScId || loadingClients}
          >
            {loadingClients ? 'Loading…' : 'Load clients'}
          </button>
        </div>

        {saveError && <div className="coverage-error">{saveError}</div>}

        {clientsLoaded && (
          <>
            <div className="coverage-bulk-row">
              <span className="coverage-muted">{scClients.length} client{scClients.length !== 1 ? 's' : ''}</span>
              <div className="coverage-bulk-assign">
                <span>Bulk assign all uncovered to:</span>
                <select
                  value={bulkCoveringId}
                  onChange={(e) => setBulkCoveringId(e.target.value)}
                  className="coverage-select"
                >
                  <option value="">Select SC…</option>
                  {staffOptions.filter((s) => String(s.id) !== selectedScId).map((s) => (
                    <option key={s.id} value={String(s.id)}>{staffLabel(s)}</option>
                  ))}
                </select>
                <button type="button" className="btn-cancel" onClick={handleBulkApply} disabled={!bulkCoveringId}>
                  Apply
                </button>
              </div>
            </div>

            {scClients.length === 0 ? (
              <p className="coverage-muted">This SC has no active clients.</p>
            ) : (
              <div className="coverage-table-wrap">
                <table className="coverage-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>EI #</th>
                      <th>DOB</th>
                      <th>Covering SC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scClients.map((client) => {
                      const covLabel = currentCoveringLabel(client);
                      const pendingVal = pending[client.id];
                      const selectVal = pendingVal !== undefined ? pendingVal : (client.covering_staff_id ? String(client.covering_staff_id) : '');
                      return (
                        <tr key={client.id} className={covLabel ? 'coverage-row-assigned' : ''}>
                          <td>{[client.last_name, client.first_name].filter(Boolean).join(', ')}</td>
                          <td>{client.ei_number || '—'}</td>
                          <td>{client.date_of_birth ? formatDisplayDate(client.date_of_birth) : '—'}</td>
                          <td>
                            <select
                              value={selectVal}
                              onChange={(e) => setPending((p) => ({ ...p, [client.id]: e.target.value }))}
                              className="coverage-assign-select"
                            >
                              <option value="">Unassigned</option>
                              {staffOptions.filter((s) => String(s.id) !== selectedScId).map((s) => (
                                <option key={s.id} value={String(s.id)}>{staffLabel(s)}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {pendingCount > 0 && (
              <div className="coverage-save-row">
                <span className="coverage-muted">{pendingCount} pending change{pendingCount !== 1 ? 's' : ''}</span>
                <button type="button" className="btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save coverage assignments'}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Active coverage ── */}
      <section className="coverage-card" style={{ marginTop: '1.5rem' }}>
        <h2 className="coverage-section-title">Active coverage</h2>
        {loadingActive ? (
          <p className="coverage-muted">Loading…</p>
        ) : activeError ? (
          <p className="coverage-error">{activeError}</p>
        ) : activeRows.length === 0 ? (
          <p className="coverage-muted">No active coverage assignments.</p>
        ) : (
          <>
            {selectedIds.size > 0 && (
              <div className="coverage-bulk-remove-bar">
                <span className="coverage-muted">{selectedIds.size} selected</span>
                <button type="button" className="coverage-btn-remove-selected" onClick={handleRemoveSelected}>
                  Remove selected ({selectedIds.size})
                </button>
              </div>
            )}
            <div className="coverage-table-wrap">
              <table className="coverage-table">
                <thead>
                  <tr>
                    <th className="coverage-th-check">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={sortedActiveRows.length > 0 && selectedIds.size === sortedActiveRows.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="coverage-th-sortable" onClick={() => handleActiveSort('client')}>Client {sortArrow('client')}</th>
                    <th className="coverage-th-sortable" onClick={() => handleActiveSort('primary')}>Primary SC {sortArrow('primary')}</th>
                    <th className="coverage-th-sortable" onClick={() => handleActiveSort('covering')}>Covering SC {sortArrow('covering')}</th>
                    <th className="coverage-th-sortable" onClick={() => handleActiveSort('return')}>Expected return {sortArrow('return')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedActiveRows.map((r) => (
                    <tr key={r.id} className={selectedIds.has(r.id) ? 'coverage-row-selected' : ''}>
                      <td className="coverage-td-check">
                        <input
                          type="checkbox"
                          aria-label={`Select ${[r.client_last_name, r.client_first_name].filter(Boolean).join(', ')}`}
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleSelect(r.id)}
                        />
                      </td>
                      <td>{[r.client_last_name, r.client_first_name].filter(Boolean).join(', ')}</td>
                      <td>{[r.primary_staff_last_name, r.primary_staff_first_name].filter(Boolean).join(', ') || '—'}</td>
                      <td>{[r.covering_staff_last_name, r.covering_staff_first_name].filter(Boolean).join(', ')}</td>
                      <td>{r.expected_end_at ? formatDisplayDate(r.expected_end_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
